import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useClientConfig } from '../lib/config.js';

/**
 * Section navigation within one organization.
 *
 * Ordered by what a teacher does, not by how the system is built. It used to
 * read Overview, Plans, Invoices, Live sessions, Members, Settings — three
 * billing items first, and the two things used every day fourth and fifth. On a
 * free plan with billing switched off, half the navigation led to a subscription
 * nobody is charged for and an empty invoice list.
 *
 * Plans and Invoices appear only when billing is actually enabled. Hidden rather
 * than disabled: a greyed-out tab still asks to be understood, and there is
 * nothing to understand here beyond "not in use yet".
 */
export function OrgNav({ orgId }: { orgId: string }): ReactNode {
  const config = useClientConfig();

  const tabs = [
    { to: `/orgs/${orgId}`, label: 'Dashboard', end: true },
    { to: `/orgs/${orgId}/live-sessions`, label: 'Classes' },
    /*
     * "Students", not "Members". That page is codes, the register of who
     * claimed one, and invitations — a teacher calls those people students, and
     * "members" is our word for our data model.
     */
    { to: `/orgs/${orgId}/members`, label: 'Students' },
    ...(config.billingEnabled
      ? [
          { to: `/orgs/${orgId}/plans`, label: 'Plans' },
          { to: `/orgs/${orgId}/invoices`, label: 'Invoices' },
        ]
      : []),
    { to: `/orgs/${orgId}/settings`, label: 'Settings' },
  ];

  return (
    <nav className="tabs">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end ?? false}
          className={({ isActive }) => `tab${isActive ? ' active' : ''}`}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
